import { Component, Input, OnInit } from '@angular/core';
import { Message } from '../../../../classes/Message';
import { CustomerService, UserService } from '../../../../services';
import { MessageService, SelectItem } from 'primeng';
import { ICustomerUserInfo } from '../../../../interfaces/telephony/customer/customer-user-info.interface';
import { ICustomerStructureData } from '../../../../interfaces/telephony/customer/customer-structure-data.dto.interface';

@Component({
  selector: 'app-customer-users-info',
  templateUrl: './customer-users-info.component.html',
  styleUrls: ['customer-users-info.component.scss'],
})
export class CustomerUsersInfoComponent implements OnInit {
  constructor(
    private customerService: CustomerService,
    private messageService: MessageService,
    private userService: UserService,
  ) {}

  @Input() customerId: number;
  @Input() currentCallerUserId: string;

  private _structure: ICustomerStructureData;
  @Input()
  get structure(): ICustomerStructureData {
    return this._structure;
  }

  set structure(value: ICustomerStructureData) {
    if (this._structure !== value) {
      this._structure = value;

      this.setDeptsAndTeams();
    }
  }

  isBusy = false;
  viewCurrentCaller = false;

  usersInfo: ICustomerUserInfo[];

  // for use in isolating current caller user
  filteredUsersInfo: ICustomerUserInfo[];

  selectedUserName: string;
  userOrgStructure: ICustomerProfileUserOrgStructure;
  userOrgDialogVisible = false;
  isAllTeamsAccess: boolean = false;
  defaultTeam: ICustomerProfileUserTeam | null = null;
  loadingOrgStructure = false;
  searchText: string;

  depts: SelectItem[] = [];
  teams: SelectItem[] = [];

  selectedDeptId: number | null = null;
  selectedTeamId: number | null = null;

  ngOnInit() {
    this.loadInformation();
  }

  setDeptsAndTeams() {
    this.depts = this.structure.departments.map((x) => {
      return { label: x.name, value: x.id };
    });
    this.depts.unshift({ label: 'Any Department', value: null });
  }

  onSelectDept(deptId: any) {
    if (!deptId) {
      this.teams = [];
      this.selectedDeptId = deptId;
      this.onFilterChanged();
      return;
    }

    const dept = this.structure.departments.find((x) => x.id == deptId);
    this.teams = dept?.teams?.map((x) => {
      return { label: x.name, value: x.id };
    });
    this.teams.unshift({ label: 'Any Team', value: null });

    this.selectedDeptId = deptId;
    this.onFilterChanged();
  }

  onSelectTeam(teamId: any) {
    this.selectedTeamId = teamId;
    this.onFilterChanged();
  }

  loadInformation() {
    this.isBusy = true;

    this.customerService
      .getUsersInfo(this.customerId)
      .then((res) => {
        this.usersInfo = res;
        this.isBusy = false;

        if (this.currentCallerUserId) {
          this.viewCurrentCaller = true;
        }

        this.filterCurrentCallUser();
      })
      .catch((err) => {
        this.messageService.add(
          Message.Error('Load Customer Users', 'An error occurred loading customer users info'),
        );
        this.isBusy = false;
      });
  }

  filterCurrentCallUser() {
    if (this.viewCurrentCaller) {
      this.filteredUsersInfo = this.usersInfo.filter((x) => x.userId == this.currentCallerUserId);
    } else {
      this.onFilterChanged();
    }
  }

  showUserOrgStructure(user: ICustomerUserInfo) {
    this.loadingOrgStructure = true;

    this.selectedUserName = user.userName;
    this.userService
      .getCustomerProfileUserOrgStructure(user.userId)
      .then((res) => {
        this.userOrgStructure = res;
        this.initializeStructure();

        console.log('Loaded org structure: ', res);
        this.loadingOrgStructure = false;
      })
      .catch(() => {
        this.loadingOrgStructure = false;
      });

    this.userOrgDialogVisible = true;
  }

  initializeStructure() {
    this.isAllTeamsAccess = this.userOrgStructure.departments.every(
      (dept) => this.getTeamsForDepartment(dept).length === this.userOrgStructure.teams.length,
    );
    this.userOrgStructure.departments.forEach((dept) => {
      dept.expanded = true;
    });
    this.defaultTeam = this.userOrgStructure.teams.find((team) => team.isDefault);
  }

  toggleDepartment(department: ICustomerProfileUserDepartment) {
    department.expanded = !department.expanded;
  }

  getTeamsForDepartment(department: ICustomerProfileUserDepartment) {
    return this.userOrgStructure.teams.filter((team) => team.departmentId === department.id);
  }

  searchTextChanged(text: string): void {
    text = text || '';
    this.searchText = text;

    if (!this.viewCurrentCaller) {
      this.filteredUsersInfo = this.usersInfo.filter((x) =>
        x.userName.toLowerCase().startsWith(this.searchText.toLowerCase()),
      );
    }
  }

  private onFilterChanged() {
    if (this.viewCurrentCaller) {
      return;
    }
    if (this.selectedTeamId) {
      this.filteredUsersInfo = this.usersInfo.filter((x) =>
        x.associatedTeamIds.includes(this.selectedTeamId),
      );
    } else if (this.selectedDeptId) {
      this.filteredUsersInfo = this.usersInfo.filter((x) =>
        x.associatedDeptIds.includes(this.selectedDeptId),
      );
    } else {
      this.filteredUsersInfo = this.usersInfo;
    }
  }

  resetFilters() {
    this.selectedDeptId = null;
    this.selectedTeamId = null;
    this.searchText = '';
    this.viewCurrentCaller = false;
    this.onFilterChanged();
  }
}
